export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  [key: string]: string | undefined;
};

const UTM_SESSION_STORAGE_KEY = 'wwg:utm';

function readUtmFromSessionStorage(): UtmParams {
  try {
    const raw = window.sessionStorage.getItem(UTM_SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as UtmParams;
  } catch {
    return {};
  }
}

function writeUtmToSessionStorage(utm: UtmParams) {
  try {
    window.sessionStorage.setItem(UTM_SESSION_STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // Ignore storage errors (Safari private mode, quotas, etc.)
  }
}

/**
 * Extracts UTM parameters from the given search string.
 * Defaults to `window.location.search`.
 */
export function getUtmParams(
  search: string = window.location.search,
): UtmParams {
  const params = new URLSearchParams(search);
  const utm: UtmParams = {};

  // Canonical UTM keys
  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ] as const;
  for (const key of keys) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }

  // Also include any additional utm_* keys present
  // Using forEach instead of entries() iteration to stay compatible with lower TS targets.
  params.forEach((value, key) => {
    if (key.startsWith('utm_') && value && utm[key] == null) {
      utm[key] = value;
    }
  });

  return utm;
}

/**
 * Returns UTM params for the current session.
 *
 * - If the current URL has utm_* params, they are returned and persisted into sessionStorage.
 * - If the current URL has no utm_* params, the previously persisted session UTMs are returned.
 *
 * This prevents UTMs from "disappearing" mid-session when SPA navigation or redirects remove
 * the query string.
 */
export function getSessionUtmParams(
  search: string = window.location.search,
): UtmParams {
  const fromUrl = getUtmParams(search);
  const fromSession = readUtmFromSessionStorage();

  // Merge so that URL UTMs (if present) override stored ones.
  const merged: UtmParams = { ...fromSession, ...fromUrl };

  // Persist only if URL provides at least one UTM (i.e., a "first load" or a new campaign).
  if (Object.keys(fromUrl).length > 0) {
    writeUtmToSessionStorage(merged);
  }

  return merged;
}
